import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateSetup, type SetupDevice, type SetupPath, type SetupStatus } from '../../hooks/useSetup.ts';
import { DevicePicker } from './DevicePicker.tsx';
import { HistoryImportButton } from './HistoryImport.tsx';
import { SetupPaths } from './SetupPaths.tsx';
import { MUTED, PRIMARY_BUTTON, SECONDARY_BUTTON } from './styles.ts';

export interface StepProps {
  setup: SetupStatus;
  onNext: () => void;
}

function StepHeader({ title, body }: Readonly<{ title: string; body?: string }>) {
  return (
    <div className="flex flex-col gap-1">
      <h1 className="text-2xl font-bold text-(--color-text-primary)">{title}</h1>
      {body && <p className={MUTED}>{body}</p>}
    </div>
  );
}

function StepFooter({ onSkip, children }: Readonly<{ onSkip?: () => void; children: ReactNode }>) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
      {onSkip && (
        <button type="button" onClick={onSkip} className="mr-auto text-sm text-(--color-text-secondary) hover:underline">
          {t('setup.wizard.skip')}
        </button>
      )}
      {children}
    </div>
  );
}

/** Step 1: "What do you ride with?" */
export function DevicesStep({ setup, onNext }: Readonly<StepProps>) {
  const { t } = useTranslation();
  const update = useUpdateSetup();
  const [devices, setDevices] = useState<SetupDevice[]>(setup.devices);
  return (
    <>
      <StepHeader title={t('setup.devices.title')} body={t('setup.devices.body')} />
      <DevicePicker value={devices} onChange={setDevices} />
      <StepFooter onSkip={onNext}>
        <button
          type="button"
          disabled={devices.length === 0 || update.isPending}
          onClick={() => update.mutate({ devices }, { onSuccess: onNext })}
          className={PRIMARY_BUTTON}
        >
          {t('setup.wizard.continue')}
        </button>
      </StepFooter>
    </>
  );
}

/** Step 2: one card per recommended path, live status from polling. */
export function ConnectStep({ setup, onNext }: Readonly<StepProps>) {
  const { t } = useTranslation();
  const anyConnected = setup.has_rides || setup.paths.some((p) => p.status !== 'not_connected');
  return (
    <>
      <StepHeader title={t('setup.connect.title')} body={t('setup.connect.body')} />
      <SetupPaths paths={setup.paths} embedConnect />
      <StepFooter>
        <button type="button" onClick={onNext} className={PRIMARY_BUTTON}>
          {anyConnected ? t('setup.wizard.continue') : t('setup.wizard.skipForNow')}
        </button>
      </StepFooter>
    </>
  );
}

type HistoryWindow = 'garmin' | 'intervals' | 'health';

function historyWindows(paths: readonly SetupPath[]): HistoryWindow[] {
  const ids = new Set(paths.map((p) => p.id));
  const windows: HistoryWindow[] = [];
  if (ids.has('garmin_connect')) windows.push('garmin');
  if (ids.has('intervals')) windows.push('intervals');
  if ([...ids].some((id) => id.startsWith('health'))) windows.push('health');
  return windows;
}

/** Step 3: why older rides are missing, and how to bring them. */
export function HistoryStep({ setup, onNext }: Readonly<StepProps>) {
  const { t } = useTranslation();
  const update = useUpdateSetup();
  const windows = historyWindows(setup.paths);
  return (
    <>
      <StepHeader title={t('setup.history.title')} />
      {windows.length > 0 && (
        <ul className={`${MUTED} list-disc pl-5 flex flex-col gap-1`}>
          {windows.map((w) => (
            <li key={w}>{t(`setup.history.windows.${w}`)}</li>
          ))}
        </ul>
      )}
      <p className="text-sm text-(--color-text-primary)">{t('setup.history.explain')}</p>
      {setup.history.zip_import === 'pro' && <p className={MUTED}>{t('setup.history.proNote')}</p>}
      <StepFooter onSkip={onNext}>
        <button
          type="button"
          disabled={update.isPending}
          onClick={() => update.mutate({ history_skipped: true }, { onSuccess: onNext })}
          className={SECONDARY_BUTTON}
        >
          {t('setup.history.later')}
        </button>
        <HistoryImportButton zipImport={setup.history.zip_import} className={PRIMARY_BUTTON}>
          {t('setup.history.import')}
        </HistoryImportButton>
      </StepFooter>
    </>
  );
}

/** Step 4: what happens next; finishing marks onboarding complete. */
export function DoneStep({ setup, onFinish, finishing }: Readonly<{ setup: SetupStatus; onFinish: () => void; finishing: boolean }>) {
  const { t } = useTranslation();
  return (
    <>
      <StepHeader title={t('setup.done.title')} body={t('setup.done.body')} />
      {setup.rides > 0 && (
        <output className="rounded-xl bg-(--color-accent)/10 border border-(--color-accent)/30 px-4 py-3 text-sm text-(--color-accent)">
          {t('setup.status.ridesIn', { count: setup.rides })}
        </output>
      )}
      <StepFooter>
        <button type="button" disabled={finishing} onClick={onFinish} className={PRIMARY_BUTTON}>
          {t('setup.done.finish')}
        </button>
      </StepFooter>
    </>
  );
}
